import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Mail } from "lucide-react";

// Resolves the destination route for a user based on their role. Mirrors the
// logic in Login.jsx / Dashboard.jsx so users land directly in their workspace
// after verifying their email — no extra click required.
function destinationForRole(role) {
  if (role === "talent") return "/talent";
  if (role === "creator" || role === "professional") return "/createurs";
  if (role === "admin") return "/admin";
  return "/dashboard";
}

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const token = params.get("token");
  // States: loading | success | already | expired | invalid | error
  const [state, setState] = useState("loading");
  const [message, setMessage] = useState("");
  const [destination, setDestination] = useState("/dashboard");
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      setMessage("Lien invalide — aucun token fourni.");
      return;
    }
    // Guard against React StrictMode double-invocation.
    if (calledRef.current) return;
    calledRef.current = true;

    api.post("/auth/verify-email", { token, origin: window.location.origin })
      .then(({ data }) => {
        // Case A: already verified (idempotent re-click / mail-scanner prefetch).
        // Backend returns 200 with { already_verified: true } and NO session_token.
        if (data.already_verified) {
          setState("already");
          setMessage(data.message || "Email déjà confirmé. Connectez-vous pour accéder à votre compte.");
          return;
        }
        // Case B: first valid use — auto-login + redirect to role dashboard.
        if (data.session_token) localStorage.setItem("helencia_token", data.session_token);
        if (data.user) {
          setUser(data.user);
          setDestination(destinationForRole(data.user.role));
        }
        // Best-effort: re-sync auth state (loads talent_profile / pro_profile).
        checkAuth();
        setState("success");
        // Auto-redirect after a short delay so the user sees the confirmation.
        setTimeout(() => {
          navigate(destinationForRole(data.user?.role), { replace: true });
        }, 1800);
      })
      .catch((e) => {
        const detail = typeof e.response?.data?.detail === "string" ? e.response.data.detail : null;
        if (detail && detail.toLowerCase().includes("expir")) {
          setState("expired");
          setMessage(detail);
        } else if (detail) {
          setState("invalid");
          setMessage(detail);
        } else {
          setState("error");
          setMessage(e.userMessage || "Une erreur est survenue. Réessayez dans un instant.");
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Render helpers --------------------------------------------------
  const renderSuccess = () => (
    <div className="rounded-3xl border border-[#E6C29A] bg-gradient-to-br from-[#FAF3EA] to-white p-8 text-center" data-testid="verify-success">
      <div className="w-12 h-12 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-4">
        <CheckCircle2 className="w-6 h-6 text-[#D4A574]" />
      </div>
      <p className="font-serif text-2xl text-[#1A1A1A]">Votre email est confirmé.</p>
      <p className="text-sm text-[#6E4B2A] mt-2">Redirection vers votre espace Helencia…</p>
      <Button onClick={() => navigate(destination, { replace: true })} className="mt-6 w-full rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-12" data-testid="verify-go-dashboard">
        Accéder maintenant
      </Button>
    </div>
  );

  const renderAlready = () => (
    <div className="rounded-3xl border border-[#E6C29A] bg-gradient-to-br from-[#FAF3EA] to-white p-8 text-center" data-testid="verify-already">
      <div className="w-12 h-12 rounded-full bg-[#FAF3EA] border border-[#E6C29A] flex items-center justify-center mx-auto mb-4">
        <Mail className="w-6 h-6 text-[#A26F3E]" />
      </div>
      <p className="font-serif text-2xl text-[#1A1A1A]">Email déjà confirmé.</p>
      <p className="text-sm text-[#6E4B2A] mt-2">{message}</p>
      <Button onClick={() => navigate("/login")} className="mt-6 w-full rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-12" data-testid="verify-go-login">
        Aller à la connexion
      </Button>
    </div>
  );

  const renderError = (testid, title) => (
    <div className="rounded-3xl border border-[#F2C0B7] bg-[#FEEFEC] p-8 text-center" data-testid={testid}>
      <div className="w-12 h-12 rounded-full bg-[#A3483F] flex items-center justify-center mx-auto mb-4">
        <XCircle className="w-6 h-6 text-white" />
      </div>
      <p className="font-serif text-2xl text-[#1A1A1A]">{title}</p>
      <p className="text-sm text-[#6E4B2A] mt-2">{message}</p>
      <div className="flex flex-col gap-2 mt-6">
        <Link to="/login">
          <Button className="w-full rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-12">Aller à la connexion</Button>
        </Link>
        {state === "expired" && (
          <Link to="/login" className="text-xs text-[#A26F3E] hover:text-[#1A1A1A] mt-2">
            Depuis la connexion, demandez un nouveau lien.
          </Link>
        )}
      </div>
    </div>
  );

  // Title varies per state to surface clarity in the header zone.
  const title = state === "success" ? "Bienvenue à bord !"
    : state === "already" ? "C'est déjà fait."
    : state === "expired" ? "Lien expiré"
    : state === "invalid" ? "Lien invalide"
    : state === "error" ? "Erreur"
    : "Vérification de votre email";

  return (
    <AuthShell eyebrow="Confirmation" title={title} subtitle={state === "loading" ? "Un instant…" : ""}>
      {state === "loading" && (
        <div className="text-center py-10" data-testid="verify-loading">
          <Loader2 className="w-8 h-8 text-[#C68E59] animate-spin mx-auto" />
        </div>
      )}
      {state === "success" && renderSuccess()}
      {state === "already" && renderAlready()}
      {state === "expired" && renderError("verify-expired", message || "Lien expiré")}
      {state === "invalid" && renderError("verify-invalid", message || "Lien invalide")}
      {state === "error" && renderError("verify-error", "Une erreur est survenue")}
    </AuthShell>
  );
}
